import type { TenantSiteEditorSectionId } from '../../../components/tenantSite/TenantSiteRenderer';

export type SiteEditorSection = {
  id: TenantSiteEditorSectionId;
  label: string;
  description: string;
  canvasSection?: Exclude<TenantSiteEditorSectionId, 'style' | 'seo'>;
};

export const SITE_EDITOR_SECTIONS: SiteEditorSection[] = [
  { id: 'style', label: 'Style', description: 'Site colors' },
  { id: 'branding', label: 'Navbar', description: 'Name and logo', canvasSection: 'branding' },
  { id: 'hero', label: 'Spotlight', description: 'Headline and image', canvasSection: 'hero' },
  { id: 'services', label: 'Services', description: 'Service presentation', canvasSection: 'services' },
  { id: 'gallery', label: 'Gallery', description: 'Photos and captions', canvasSection: 'gallery' },
  { id: 'team', label: 'Team', description: 'Section visibility', canvasSection: 'team' },
  { id: 'reviews', label: 'Reviews', description: 'Customer reviews', canvasSection: 'reviews' },
  { id: 'about', label: 'About', description: 'Business story', canvasSection: 'about' },
  { id: 'faq', label: 'FAQ', description: 'Common questions', canvasSection: 'faq' },
  { id: 'hours', label: 'Hours', description: 'Public opening hours', canvasSection: 'hours' },
  { id: 'booking', label: 'Booking', description: 'Booking options', canvasSection: 'booking' },
  { id: 'contact', label: 'Contact', description: 'Location and links', canvasSection: 'contact' },
  { id: 'seo', label: 'Search', description: 'Search and sharing' },
];

export const SITE_EDITOR_SECTION_MAP = Object.fromEntries(
  SITE_EDITOR_SECTIONS.map((section) => [section.id, section])
) as Record<TenantSiteEditorSectionId, SiteEditorSection>;

export const isTenantSiteEditorSectionId = (value: string): value is TenantSiteEditorSectionId =>
  SITE_EDITOR_SECTIONS.some((section) => section.id === value);

const ISSUE_SECTION_PREFIXES: Array<[string, TenantSiteEditorSectionId]> = [
  ['theme', 'style'],
  ['business.displayName', 'branding'],
  ['assets.logo', 'branding'],
  ['homepage.hero', 'hero'],
  ['assets.hero', 'hero'],
  ['assets.heroVideo', 'hero'],
  ['booking.showPrices', 'services'],
  ['homepage.gallery', 'gallery'],
  ['sections.showGallery', 'gallery'],
  ['sections.showTeam', 'team'],
  ['homepage.reviews', 'reviews'],
  ['sections.showReviews', 'reviews'],
  ['homepage.about', 'about'],
  ['sections.showAbout', 'about'],
  ['homepage.faq', 'faq'],
  ['sections.showFaq', 'faq'],
  ['business.businessHours', 'hours'],
  ['booking', 'booking'],
  ['business.address', 'contact'],
  ['business.googlePlaceId', 'contact'],
  ['business.phone', 'contact'],
  ['business.email', 'contact'],
  ['business.website', 'contact'],
  ['socials', 'contact'],
  ['metadata', 'seo'],
];

export const siteEditorSectionForIssuePath = (
  issuePath: string | undefined,
  message = ''
): TenantSiteEditorSectionId | undefined => {
  const path = (issuePath === 'content' ? message.match(/content\.([\w.]+)/)?.[1] : issuePath)
    ?.replace(/^content\./, '');
  if (!path) return undefined;
  return ISSUE_SECTION_PREFIXES.find(([prefix]) =>
    path === prefix || path.startsWith(`${prefix}.`))?.[1];
};
